import React, { useState, useEffect } from 'react';

/**
 * IconPicker — opens a modal to browse Image Manager folders and pick an image.
 * Usage: <IconPicker API={API} adminToken={adminToken} onPick={(url) => ...} onClose={() => ...} />
 */
export default function IconPicker({ API, adminToken, onPick, onClose, isDarkMode }) {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [folderSearch, setFolderSearch] = useState('');

  useEffect(() => {
    fetchFolders();
  }, []);

  const fetchFolders = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${adminToken}` };
      // Fetch all 3 sources in parallel: dynamic folders + legacy icons + legacy avatars
      const [foldersRes, iconsRes, avatarsRes] = await Promise.all([
        fetch(`${API}/admin/image-folders`, { headers }),
        fetch(`${API}/admin/icons`, { headers }).catch(() => null),
        fetch(`${API}/admin/avatar-images`, { headers }).catch(() => null),
      ]);
      const foldersData = await foldersRes.json();
      const dynamicFolders = (foldersData.folders || []).map(f => ({ ...f, _source: 'folder' }));

      // Add legacy "Icons" collection as a virtual folder
      const legacyFolders = [];
      if (iconsRes && iconsRes.ok) {
        const iconsData = await iconsRes.json();
        const iconsList = iconsData.icons || [];
        if (iconsList.length > 0) {
          legacyFolders.push({
            id: '__legacy_icons__',
            name: 'Icons',
            icon: '🎭',
            asset_count: iconsList.length,
            _source: 'icons',
            _assets: iconsList,
          });
        }
      }
      if (avatarsRes && avatarsRes.ok) {
        const avatarsData = await avatarsRes.json();
        const avatarsList = avatarsData.avatar_images || avatarsData.images || [];
        if (avatarsList.length > 0) {
          legacyFolders.push({
            id: '__legacy_avatars__',
            name: 'Avatars',
            icon: '👤',
            asset_count: avatarsList.length,
            _source: 'avatars',
            _assets: avatarsList,
          });
        }
      }

      setFolders([...legacyFolders, ...dynamicFolders]);
    } catch (e) {
      console.error('Failed to load folders:', e);
    }
    setLoading(false);
  };

  const fetchAssets = async (folder) => {
    setSelectedFolder(folder);
    setAssets([]);
    setFolderSearch('');
    setLoading(true);

    // Legacy folders already have assets loaded
    if (folder._assets) {
      setAssets(folder._assets.map(a => ({
        id: a.id,
        name: a.name || a.filename,
        url: a.url,
        tags: a.tags || [],
      })));
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API}/admin/image-assets/${folder.id}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await res.json();
      setAssets(data.assets || []);
    } catch (e) {
      console.error('Failed to load assets:', e);
    }
    setLoading(false);
  };

  const filteredFolders = folderSearch
    ? folders.filter(f => f.name?.toLowerCase().includes(folderSearch.toLowerCase()))
    : folders;

  const filteredAssets = search
    ? assets.filter(a => a.name?.toLowerCase().includes(search.toLowerCase()) || a.tags?.some(t => t.toLowerCase().includes(search.toLowerCase())))
    : assets;

  const bg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const text = isDarkMode ? 'text-gray-100' : 'text-gray-900';
  const subtext = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const hoverBg = isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50';
  const inputBg = isDarkMode ? 'bg-gray-700 text-gray-100 border-gray-600' : 'bg-white text-gray-900 border-gray-300';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4" onClick={onClose}>
      <div className={`${bg} rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${border}`}>
          <div className="flex items-center gap-3">
            {selectedFolder && (
              <button onClick={() => { setSelectedFolder(null); setAssets([]); setSearch(''); }} className={`text-sm ${subtext} hover:${text}`}>
                ← Back
              </button>
            )}
            <h3 className={`font-bold ${text}`}>
              {selectedFolder ? selectedFolder.name : 'Pick an Icon'}
            </h3>
          </div>
          <button onClick={onClose} className={`w-8 h-8 rounded-full flex items-center justify-center ${subtext} ${hoverBg}`}>✕</button>
        </div>

        {/* Search */}
        <div className={`px-4 pt-3 border-b ${border} pb-3`}>
          <input
            type="text"
            placeholder={selectedFolder ? 'Search images...' : 'Search folders...'}
            value={selectedFolder ? search : folderSearch}
            onChange={e => selectedFolder ? setSearch(e.target.value) : setFolderSearch(e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border text-sm ${inputBg}`}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className={`text-center py-10 ${subtext}`}>Loading...</div>
          ) : !selectedFolder ? (
            /* Folder grid */
            filteredFolders.length === 0 ? (
              <div className={`text-center py-10 ${subtext}`}>
                <p className="text-3xl mb-2">📁</p>
                <p>{folderSearch ? 'No matching folders' : 'No image folders found'}</p>
                {!folderSearch && <p className="text-xs mt-1">Add folders in Image Manager first</p>}
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {filteredFolders.map(folder => (
                  <button
                    key={folder.id}
                    onClick={() => fetchAssets(folder)}
                    className={`p-3 rounded-xl border ${border} ${hoverBg} flex flex-col items-center gap-1.5 transition-colors`}
                  >
                    <span className="text-2xl">{folder.icon || '📁'}</span>
                    <span className={`text-[11px] font-medium ${text} text-center leading-tight line-clamp-2`}>{folder.name}</span>
                    <span className={`text-[10px] ${subtext}`}>{folder.asset_count || 0}</span>
                  </button>
                ))}
              </div>
            )
          ) : (
            /* Asset grid */
            filteredAssets.length === 0 ? (
              <div className={`text-center py-10 ${subtext}`}>
                <p className="text-3xl mb-2">🖼️</p>
                <p>{search ? 'No matching images' : 'No images in this folder'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
                {filteredAssets.map(asset => (
                  <button
                    key={asset.id}
                    onClick={() => onPick(asset.url)}
                    className={`group p-2 rounded-xl border ${border} ${hoverBg} flex flex-col items-center gap-1.5 transition-all hover:shadow-md hover:border-teal-400`}
                  >
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                      <img src={`${API.replace('/api', '')}${asset.url}`} alt={asset.name} className="w-full h-full object-contain" />
                    </div>
                    <span className={`text-[10px] ${subtext} text-center leading-tight truncate w-full`}>{asset.name}</span>
                  </button>
                ))}
              </div>
            )
          )}
        </div>

        {/* Footer with clear option */}
        <div className={`p-3 border-t ${border} flex justify-between items-center`}>
          <button
            onClick={() => onPick('')}
            className={`text-xs ${subtext} ${hoverBg} px-3 py-1.5 rounded-lg`}
          >
            Reset to default emoji
          </button>
          <span className={`text-[10px] ${subtext}`}>
            {selectedFolder ? `${filteredAssets.length} images` : `${filteredFolders.length} of ${folders.length} folders`}
          </span>
        </div>
      </div>
    </div>
  );
}
